/**
 * Fetches data with automatic retry logic and timeout handling
 * @param url - The URL to fetch
 * @param options - Fetch options (same as native fetch)
 * @param retryConfig - Retry configuration
 * @returns Promise with the response
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retryConfig: {
    maxRetries?: number;
    retryDelay?: number;
    timeout?: number;
    retryOn?: (response: Response) => boolean;
  } = {}
): Promise<Response> {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    timeout = 10000, // 10 seconds default timeout
    retryOn = (response) => !response.ok && response.status >= 500
  } = retryConfig;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // If response is ok or not retryable, return it
        if (response.ok || !retryOn(response)) {
          return response;
        }

        // If this is the last attempt, return the response anyway
        if (attempt === maxRetries) {
          return response;
        }

        // Only log retry attempts in development
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `⚠️ Fetch failed (attempt ${attempt + 1}/${maxRetries + 1}):`,
            response.status,
            response.statusText,
            'Retrying...'
          );
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        lastError = fetchError instanceof Error ? fetchError : new Error(String(fetchError));

        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          lastError = new Error(`Request timeout after ${timeout}ms`);
        }

        // If this is the last attempt, throw the error
        if (attempt === maxRetries) {
          throw lastError;
        }

        // Wait before retrying (exponential backoff)
        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
        // Continue to next iteration
        continue;
      }

      // Wait before retrying (exponential backoff) if response was not ok
      if (attempt < maxRetries) {
        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        throw lastError;
      }

      // Only log retry attempts in development or if verbose logging is enabled
      // Don't spam console with retry messages
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `⚠️ Fetch error (attempt ${attempt + 1}/${maxRetries + 1}):`,
          lastError.message,
          'Retrying...'
        );
      }

      // Wait before retrying (exponential backoff)
      const delay = retryDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Failed to fetch after retries');
}

/**
 * Fetches JSON data with automatic retry logic
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @param retryConfig - Retry configuration
 * @returns Promise with the parsed JSON data
 */
export async function fetchJsonWithRetry<T = any>(
  url: string,
  options: RequestInit = {},
  retryConfig?: Parameters<typeof fetchWithRetry>[2]
): Promise<T> {
  const response = await fetchWithRetry(url, options, retryConfig);
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  try {
    // Clone the response so we can check content type without consuming the body
    const contentType = response.headers.get('content-type') || '';
    
    // Check if response is HTML (error page) by content type
    if (contentType.includes('text/html')) {
      const text = await response.text();
      throw new Error('Received HTML error page instead of JSON. The server may be experiencing issues.');
    }
    
    // Try to parse as JSON
    const text = await response.text();
    
    if (!text || text.trim().length === 0) {
      // Empty response - return empty object
      return {} as T;
    }
    
    // Double-check for HTML in case content-type was wrong
    const trimmed = text.trim();
    if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
      throw new Error('Received HTML error page instead of JSON. The server may be experiencing issues.');
    }
    
    return JSON.parse(text);
  } catch (error) {
    // Provide more helpful error message
    if (error instanceof Error && error.message.includes('HTML error page')) {
      throw error;
    }
    // Don't wrap JSON parse errors - they're already descriptive
    if (error instanceof SyntaxError) {
      throw new Error(`Invalid JSON response: ${error.message}`);
    }
    throw new Error(`Failed to parse JSON: ${error instanceof Error ? error.message : String(error)}`);
  }
}

